// Accessible accordion that conforms with the ARIA specification
// https://www.w3.org/WAI/ARIA/apg/patterns/accordion/examples/accordion/

import clsx from 'clsx'
import { useState, type ComponentPropsWithoutRef, type ReactElement, type ReactNode } from 'react'

import { composeEventHandlers } from '@lib/utils/compose-event-handlers'

import { IconSprites } from '@constants/enums/sprites'

import { Icon } from '@components/ui-core/icon/icon'
import { Text } from '@components/ui-core/text/text'

import styles from './accordion.module.css'

type Item = {
  id: string
  trigger: string
  content: string | ReactNode
  expandedByDefault?: boolean
}

function Item({
  data,
  expanded,
  onTriggerClick,
}: {
  data: Item
  expanded: boolean
  onTriggerClick: (id: string) => void
}): ReactElement {
  const { id, trigger, content } = data

  return (
    <div key={id} className={clsx(styles.item, expanded ? styles.isExpanded : undefined)}>
      <h3 className={styles.trigger}>
        <button
          type="button"
          aria-expanded={expanded}
          className={styles.button}
          aria-controls={`${id}-panel`}
          id={`${id}-trigger`}
          onClick={composeEventHandlers(() => onTriggerClick(id))}
        >
          <Text
            as="span"
            variant="bodyMedium"
            fontWeight={expanded ? 'semibold' : 'regular'}
            className={styles.triggerText}
          >
            {trigger}
          </Text>
          <div className={clsx(styles.icon, expanded ? styles.iconRotated : '')} aria-hidden>
            <Icon variant={IconSprites.ThinArrowDown} />
          </div>
        </button>
      </h3>
      <div
        id={`${id}-panel`}
        role="region"
        aria-labelledby={`${id}-trigger`}
        className={styles.panel}
        hidden={!expanded}
      >
        <Text as="span" variant="bodySmall">
          {content}
        </Text>
      </div>
    </div>
  )
}

interface AccordionProps extends ComponentPropsWithoutRef<'div'> {
  allowMultipleExpanded?: boolean
  data: Item[]
}

export function Accordion({
  allowMultipleExpanded = false,
  className,
  data,
  ...rest
}: AccordionProps): ReactElement {
  const [openItems, setOpenItems] = useState<string[]>(() =>
    data.filter(x => x.expandedByDefault === true).map(x => x.id),
  )

  const onTriggerClick = (id: string) => {
    const updatedOpenItems = openItems.includes(id)
      ? openItems.filter(item => item !== id)
      : [...(allowMultipleExpanded ? openItems : []), id]
    setOpenItems(updatedOpenItems)
  }

  return (
    <div className={clsx(styles.accordion, className)} {...rest}>
      {data.map(item => (
        <Item
          key={item?.id}
          data={item}
          expanded={openItems.includes(item.id)}
          onTriggerClick={onTriggerClick}
        />
      ))}
    </div>
  )
}
